;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:propstrata-app":true});}catch(e){}}();import { isTourStartBlockedForUser } from "@/features/onboarding-tours/orchestrator/get-catalog-page";
import { resolvesTourLaunchRoute } from "@/features/onboarding-tours/orchestrator/launch-tour";
import {
	applyPipelineTourEnd,
	getNextPipelineStep,
	type PipelineContext,
} from "@/features/onboarding-tours/orchestrator/onboarding-pipeline";
import { getTourDefinition } from "@/features/onboarding-tours/registry/tour-registry";
import type {
	TourEligibilityContext,
	TourEndReason,
	TourId,
	TourStartSource,
} from "@/features/onboarding-tours/types/tour-definition";
import type { OnboardingToursStorageValue } from "@/features/onboarding-tours/types/tour-persistence";

export interface TourOrchestratorState {
	storage: OnboardingToursStorageValue;
	isTourActive: boolean;
	activeTourId: TourId | null;
	pipelineContext: PipelineContext;
}

export interface StartTourRequest {
	tourId: TourId;
	source: TourStartSource;
	framing?: "default" | "add-more-providers";
}

export interface TourOrchestratorAction {
	type: "start" | "navigate-then-start" | "unavailable";
	tourId: TourId;
	route?: string;
	framing?: "default" | "add-more-providers";
}

export function syncObjectivesFromAppState({
	storage,
	hasDataProvider,
	hasInvitedMember,
}: {
	storage: OnboardingToursStorageValue;
	hasDataProvider: boolean;
	hasInvitedMember: boolean;
}): OnboardingToursStorageValue {
	return {
		...storage,
		objectives: {
			...storage.objectives,
			hasDataProvider,
			hasInvitedMember,
		},
	};
}

export function canStartTour({
	isTourActive,
}: {
	isTourActive: boolean;
}): boolean {
	return !isTourActive;
}

export function buildStartTourAction({
	workspaceId,
	tourId,
	framing = "default",
	currentPathname = "",
	activeViewNames = [],
}: {
	workspaceId: string;
	tourId: TourId;
	framing?: "default" | "add-more-providers";
	currentPathname?: string;
	activeViewNames?: string[];
}): TourOrchestratorAction | null {
	const definition = getTourDefinition(tourId);
	if (!definition) {
		return null;
	}

	const launch = definition.launch;
	const route = resolvesTourLaunchRoute({
		activeViewNames,
		currentPathname,
		launch,
		workspaceId,
	});

	if (route === "unavailable") {
		return {
			framing,
			tourId,
			type: "unavailable",
		};
	}

	if (route === null) {
		return {
			framing,
			tourId,
			type: "start",
		};
	}

	return {
		framing,
		route,
		tourId,
		type: "navigate-then-start",
	};
}

export function recordTourEnd({
	storage,
	tourId,
	reason,
	now = new Date().toISOString(),
}: {
	storage: OnboardingToursStorageValue;
	tourId: TourId;
	reason: TourEndReason;
	now?: string;
}): OnboardingToursStorageValue {
	const timestampField = reason === "complete" ? "completedAt" : "dismissedAt";
	const existingRun = storage.tourRuns[tourId] ?? {};

	return {
		...storage,
		pipeline: applyPipelineTourEnd({
			pipeline: storage.pipeline,
			reason,
			tourId,
		}),
		tourRuns: {
			...storage.tourRuns,
			[tourId]: {
				...existingRun,
				[timestampField]: now,
			},
		},
	};
}

export function resolveHandoffAction({
	workspaceId,
	completedTourId,
	eligibilityContext,
}: {
	workspaceId: string;
	completedTourId: TourId;
	eligibilityContext?: TourEligibilityContext;
}): TourOrchestratorAction | null {
	const definition = getTourDefinition(completedTourId);
	if (!definition?.handoff || completedTourId !== "add-data-provider-guard") {
		return null;
	}

	const nextTourId = definition.handoff.nextTourId;
	const nextTour = getTourDefinition(nextTourId);
	if (
		!nextTour ||
		(eligibilityContext &&
			isTourStartBlockedForUser({ eligibilityContext, tour: nextTour }))
	) {
		return null;
	}

	return buildStartTourAction({
		tourId: nextTourId,
		workspaceId,
	});
}

export function resolvePipelineAdvanceAction({
	workspaceId,
	storage,
	pipelineContext,
}: {
	workspaceId: string;
	storage: OnboardingToursStorageValue;
	pipelineContext: PipelineContext;
}): TourOrchestratorAction | null {
	const step = getNextPipelineStep({
		input: {
			context: pipelineContext,
			objectives: storage.objectives,
			pipeline: storage.pipeline,
			tourRuns: storage.tourRuns,
		},
	});

	if (!step) {
		return null;
	}

	return buildStartTourAction({
		framing: step.framing,
		tourId: step.tourId,
		workspaceId,
	});
}

export function shouldAttemptPipeline({
	isTourActive,
}: {
	isTourActive: boolean;
}): boolean {
	return !isTourActive;
}

export function deriveInvitedObjective({
	memberCount,
	tourCompleted,
}: {
	memberCount: number;
	tourCompleted: boolean;
}): boolean {
	return tourCompleted || memberCount > 1;
}

export function shouldFetchWorkspaceInviteMemberCount({
	activeTourId,
}: {
	activeTourId: TourId | null;
}): boolean {
	return activeTourId === "invite-team-member";
}

export function resolvePersistedInvitedObjective({
	memberCount,
	previousHasInvitedMember,
	tourCompleted,
}: {
	memberCount: number | null;
	previousHasInvitedMember?: boolean;
	tourCompleted: boolean;
}): boolean | undefined {
	if (tourCompleted) {
		return true;
	}

	if (memberCount === null) {
		return previousHasInvitedMember;
	}

	return deriveInvitedObjective({ memberCount, tourCompleted: false });
}

export function deriveDataProviderObjective({
	providerCount,
}: {
	providerCount: number;
}): boolean {
	return providerCount > 0;
}
